import React from "react";
import { Box, Container, Typography, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import { landingShellContainerSx } from "./auth/authScreenStyles";
import { productBrand } from "../productBrand";

export interface FooterProps {
  /** Tighter padding for /auth and podcast invite shells so the bar does not dominate the viewport. */
  compact?: boolean;
  /** Match invite landing navbar content width (1200px + horizontal padding). */
  alignWithLandingNavbar?: boolean;
}

const Footer: React.FC<FooterProps> = ({
  compact = false,
  alignWithLandingNavbar = false,
}) => {
  const { t } = useTranslation("common");
  return (
    <Box
      component="footer"
      sx={{
        py: compact ? 2 : 4,
        width: "100%",
        mt: "auto",
        flexShrink: 0,
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Container
        maxWidth={alignWithLandingNavbar ? false : "xl"}
        sx={alignWithLandingNavbar ? landingShellContainerSx : undefined}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "flex-start" },
            gap: compact ? 1.5 : 3,
            color: "text.primary",
          }}
        >
          <Box
            sx={{
              textAlign: { xs: "center", md: "left" },
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <Box sx={{ pr: compact ? 2 : 4 }}>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.95, mb: compact ? 0 : 2 }}
                >
                  © {new Date().getFullYear()} CONTENTSCOPE SpA
                </Typography>
              </Box>
              <Link
                href="/legal/privacidad"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  opacity: 0.85,
                  "&:hover": {
                    opacity: 1,
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                }}
              >
                {t("footer.privacy")}
              </Link>
              <Typography variant="body2" sx={{ opacity: 0.45 }}>
                •
              </Typography>
              <Link
                href="/legal/terminos"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  opacity: 0.85,
                  "&:hover": {
                    opacity: 1,
                    textDecoration: "underline",
                    color: "primary.main",
                  },
                }}
              >
                {t("footer.terms")}
              </Link>
            </Box>
          </Box>

          <Box
            sx={{
              textAlign: { xs: "center", md: "right" },
            }}
          >
            <Typography
              component="a"
              href={`mailto:${productBrand.supportEmail}`}
              variant="body2"
              sx={{
                color: "inherit",
                textDecoration: "none",
                opacity: 0.9,
                "&:hover": {
                  opacity: 1,
                  color: "primary.main",
                },
              }}
            >
              {t("footer.contact", { email: productBrand.supportEmail })}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
